import { EventId, OrganizationId, UsersInfoId, UsersSearchQuery } from 'common/src/generated/types';
import { Emptyable } from 'common/src/util/emptyable';
import { isNonEmptyString } from 'common/src/util/string';
import { sortBy, uniqBy } from 'lodash-es';
import * as React from 'react';
import { executeUsersSearchQuery } from '../../generated/graphqlHooks';
import { getToken } from '../../util/aws/cognito';

interface IUseUsersSearchOptions {
    hasSelected: boolean;
    eventId: Emptyable<EventId>;
    includePhone?: boolean;
    organizationId: OrganizationId;
    nameDebounced: string;
    userInfoIds: UsersInfoId[];
}

interface IUseUsersSearchResult {
    isLoading: boolean;
    usersInfos: Array<UsersSearchQuery['organization']['usersInfos']['nodes'][number]>;
    usersInfosOptions: React.ReactNode;
}

export function useUsersSearch({
    eventId,
    organizationId,
    hasSelected,
    includePhone,
    nameDebounced,
    userInfoIds
}: IUseUsersSearchOptions): IUseUsersSearchResult {
    const [isLoading, setIsLoading] = React.useState(false);
    const [organization, setOrganization] =
        React.useState<Emptyable<UsersSearchQuery['organization']>>(null);
    const usersInfos = React.useMemo(
        () =>
            sortBy(
                uniqBy(
                    (organization?.usersInfos.nodes ?? []).concat(
                        organization?.selectedUsersInfos?.nodes ?? []
                    ),
                    (ui) => ui.id
                ),
                (ui) => ui.name.toLowerCase()
            ),
        [organization]
    );
    const usersInfosOptions = React.useMemo(
        () =>
            usersInfos.map((ui) => (
                <option key={ui.id} value={ui.id}>
                    {isNonEmptyString(ui.name) ? ui.name : '-'} (
                    {includePhone ? ui.phone?.internationalFormat || '-' : ui.email})
                </option>
            )),
        [usersInfos]
    );

    React.useEffect(() => {
        (async () => {
            setIsLoading(true);
            const { organization } = await executeUsersSearchQuery(
                {
                    organizationId,
                    eventId,
                    hasSelected,
                    includePhone: includePhone || false,
                    userInfoIds,
                    name: isNonEmptyString(nameDebounced) ? nameDebounced : undefined
                },
                await getToken()
            );

            setOrganization(organization);
            setIsLoading(false);
        })();
    }, [organizationId, eventId, includePhone, nameDebounced]);

    return {
        usersInfos,
        usersInfosOptions,
        isLoading
    };
}
